/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import glamorous from 'glamorous';
import { load as loadWebFonts } from 'webfontloader';
import Responsive from 'app/components/responsive/Responsive';
import { GOLF } from 'app/constants/challengeOneSportIds';
import {
  fetchPlayerCompetitionData,
  fetchPlayerGameData,
  fetchPlayerSeasonData,
  fetchTeamCompetitionData
} from '../../api/onesport';
import decodePlayerCompetitionStats from '../../normalizers/decodePlayerCompetitionStats';
import decodePlayerSeasonStats from '../../normalizers/decodePlayerSeasonStats';
import decodeTeamCompetitionStats from '../../normalizers/decodeTeamCompetitionStats';
import decodeRacingCompetitionStats from '../../normalizers/decodeRacingCompetitionStats';
import OneScoreWidgetCoreRapidFire from './OneScoreWidgetCoreRapidFire';
import Modal from 'app/components/commons/Modal';
import * as rapidfireActions from 'app/actions/rapidfireActions';

loadWebFonts({
  google: {
    families: ['Roboto']
  }
});

class OneScoreWidgetRapidFire extends React.Component<Props, State> {
  props: Props;

  state: State = {
    data: undefined,
    error: undefined,
    isLoading: false
  };

  wrapper: HTMLElement; // eslint-disable-line react/sort-comp

  static defaultProps = {
    isRacing: false,
    renderEntity: () => null
  };

  componentDidMount() {
    const { isOpen } = this.props;

    if (isOpen) {
      this.handleFetch();
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { isOpen, sportId } = this.props;

    if ((!prevProps.isOpen && isOpen) || (!prevProps.sportId && sportId)) {
      this.handleFetch();
    }
  }

  close = () => {
    const { onClose, id } = this.props;
    this.wrapper.style['pointer-events'] = 'none';
    onClose(id);
  };

  handleFetch() {
    const { selectedPlayer, isGolf, isRacing, sportId } = this.props;

    let fetchData = selectedPlayer.isTeam ? fetchTeamCompetitionData : fetchPlayerCompetitionData;
    let decodeData = selectedPlayer.isTeam
      ? decodeTeamCompetitionStats
      : decodePlayerCompetitionStats;

    const gameOrCompetitionId = isRacing
      ? selectedPlayer.onesportGameId
      : selectedPlayer.onesportCompetitionId;

    if (isRacing) {
      fetchData = fetchPlayerGameData;
      decodeData = decodeRacingCompetitionStats;
    } else if (isGolf) {
      fetchData = fetchPlayerSeasonData;
      decodeData = decodePlayerSeasonStats;
    }

    this.setState({
      error: null,
      isLoading: true
    });

    fetchData(sportId, selectedPlayer.onesportEntityId, gameOrCompetitionId)
      .then(res => {
        this.setState({
          data: decodeData(res.data),
          isLoading: false
        });
      })
      .catch(() => {
        this.setState({
          isLoading: false,
          error: 'No record found'
        });
      });
  }

  render() {
    const { renderEntity, isGolf, isOpen, isRacing, onClose } = this.props;
    const { data, error, isLoading } = this.state;

    return (
      <div>
        <Responsive tabletLandscape desktop>
          {isLargeScreen =>
            isLargeScreen ? (
              <React.Fragment>
                {isOpen && (
                  <OneScoreWidgetCoreRapidFire
                    player={data}
                    isGolf={isGolf}
                    isLoading={isLoading}
                    isRacing={isRacing}
                    error={error}
                  />
                )}
              </React.Fragment>
            ) : (
              <React.Fragment>
                {isOpen && (
                  <Modal size="small" showCloseButton={false} isOpen>
                    <ModalInner>
                      <ModalHead>
                        <CloseButton
                          onClick={() => {
                            onClose();
                          }}
                        />
                        {renderEntity()}
                      </ModalHead>
                      <ModalContent>
                        <OneScoreWidgetCoreRapidFire
                          player={data}
                          isGolf={isGolf}
                          isLoading={isLoading}
                          isRacing={isRacing}
                          error={error}
                        />
                      </ModalContent>
                    </ModalInner>
                  </Modal>
                )}
              </React.Fragment>
            )
          }
        </Responsive>
      </div>
    );
  }
}

const connector = connect(
  (state, { competitionId }) => {
    const challenge =
      state.rapidfire.upcomingChallenges.find(c => c.competitionId === competitionId) ||
      state.challenge;

    return {
      sportId: challenge ? challenge.sportId : undefined,
      isGolf: challenge ? challenge.sportId.toString() === GOLF : false
    };
  },
  { getUpcomingChallenges: rapidfireActions.getUpcoming }
);

export default connector(OneScoreWidgetRapidFire);

type Props = {
  competitionId: string,
  selectedPlayer: any,
  isRacing: boolean,
  isGolf: boolean,
  sportId: string,
  onClose: (id: string) => void,
  getUpcomingChallenges: () => void,
};

type State = {
  data: ?Object,
  error: ?string,
  isLoading: boolean
};

const ModalInner = glamorous.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column'
}));

const ModalHead = glamorous.div({
  flexBasis: 'auto',
  flexGrow: 0,
  flexShrink: 0
});

const ModalContent = glamorous.div({
  display: 'flex',
  flexBasis: 0,
  flexDirection: 'column',
  flexGrow: 1,
  flexShrink: 1
});

const CloseButton = glamorous('button', {
  forwardProps: ['onClick'],
  withProps: {
    type: 'button'
  }
})(
  ({ theme }) => ({
    textAlign: 'right',
    backgroundColor: theme.color.white,
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
    color: theme.color.basis4,
    fontSize: theme.fontSize.large,
    height: 30,
    marginTop: 10,
    outline: 'none',
    textTransform: 'uppercase',
    width: '100%'
  }),
  'ion-ios-close'
);
